import { Divider, Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import i18n from '../../../../base-dictionary';
import * as Sentry from '@sentry/browser';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { createOrganization } from '../../../../api/organization-client/organization-client';
import { useOrganizations } from '../../../../api/organization-client/organization-client.hooks';
import type { OrganizationResponse } from '../../../../api/organization-client/organization-client.type';
import { usePortfolios } from '../../../../api/portfolio-client/portfolio-client.hooks';
import { useUser } from '../../../../api/user-client/user-client.hooks';
import { UserContext } from '../../../../contexts';
import { setOrganizationName, setSignUpStep } from '../../../../store/auth-store/auth-store.actions';
import { SignUpStep } from '../../../../store/auth-store/auth-store.type';
import { setOrganization, setPortfolio } from '../../../../store/global-store/global-store.actions';
import { baseWhite, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { H3, Text } from '../../../../ui-library/typography/typography';
import { Layout } from '../../layout';
import type { ApiError } from './create-organization.type';

export const CreateOrganization = () => {
  const { setUser } = useContext(UserContext);

  const form = useForm({
    initialValues: {
      name: '',
    },
  });

  const { name } = form.getValues();

  const { query: organizationsQuery } = useOrganizations({ enabled: false });
  const { query: portfolioQuery } = usePortfolios({ enabled: false });
  const { query: userInfoQuery } = useUser({ enabled: false });

  const createOrg = useMutation({
    mutationKey: ['create-organization'] as const,
    mutationFn: () => createOrganization({ name }),
    onSuccess: async () => {
      setOrganizationName(name);

      await handleSetUser();
      const organization = await handleSetOrganization();
      await handleSetPortfolio(organization);

      setSignUpStep(SignUpStep.Success);
    },
    onError: ({ error }: ApiError) => {
      if (error?.message) {
        form.setFieldError('name', error.message);
      } else {
        form.setFieldError('name', 'An error occurred while creating your organization');
      }
    },
  });

  const handleSetUser = async () => {
    try {
      const { data: user } = await userInfoQuery.refetch();

      if (!user) {
        return;
      }

      setUser(user);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleSetOrganization = async () => {
    try {
      const { data: organizations } = await organizationsQuery.refetch();
      const organization = organizations?.find((org) => org.name === name);

      if (organization) {
        setOrganization(organization);
      }

      return organization;
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleSetPortfolio = async (organization?: OrganizationResponse) => {
    try {
      const { data: portfolios } = await portfolioQuery.refetch();
      const portfolio = portfolios?.find((portfolio) => portfolio.organization?.id === organization?.id);

      if (portfolio) {
        setPortfolio(portfolio);
      }

      return portfolio;
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleSubmit = async () => {
    createOrg.mutate();
  };

  return (
    <Layout aria-label="Create Organization Form">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={32}>
          <Group gap={16}>
            <Icon name="add_business" size={32} />
            <H3 id="form-title">{i18n.t('common.form.create_organization')}</H3>
          </Group>
          <Text>{i18n.t('common.form.organization_description')}</Text>
          <Divider />
          <TextInput
            label={i18n.t('common.form.organization_name')}
            placeholder={i18n.t('common.placeholders.organization')}
            {...form.getInputProps('name')}
            w="100%"
            required
            aria-labelledby="form-title"
          />
          <Divider />
          <Button
            type="submit"
            loaderProps={{ size: 'xs', color: secondaryBase }}
            w="100%"
            radius="xl"
            rightSection={<Icon name="arrow_forward" size={18} color={name ? baseWhite : skyDark} />}
            disabled={!name}
          >
            {i18n.t('common.form.create_organization')}
          </Button>
        </Stack>
      </form>
    </Layout>
  );
};
